// ImageFieldComponent (2025-06-05) – no 1×1 placeholder, no flicker


import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useFieldMetadata } from '../../contexts/FieldMetadataContext';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import { useAssetPreview } from '../../AssetManager/useAssetPreview';
import {
  getAssetReferenceId,
  isBrowserUrl,
  normalizeImageFieldMetadata,
} from '../../utils/assetFieldMetadata';

import '../../AssetManager/styles/assetCard.css';
import '../../AssetManager/styles/assetManager.css';

/* ──────────────────────────────────────────────────────────────
   Runtime detector
   - Public preview/headless screenshot should not call authenticated
     asset preview APIs.
   - Builder/editor can use backend signed preview URLs by assetId.
  ────────────────────────────────────────────────────────────── */
const readIsPublicPreviewRuntime = () => {
  try {
    if (typeof window === 'undefined') return false;

    const raw = window.sessionStorage?.getItem('isPublicPreview');

    if (raw === 'true') return true;
    if (!raw) return false;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && parsed.value === true;
  } catch (_) {
    return false;
  }
};

const ImageFieldComponent = ({ fieldId }) => {
  const { fieldsMetadata } = useFieldMetadata();

  // Ensure fieldMetadata is always defined
  const rawFieldMetadata = fieldsMetadata[fieldId] || {};
  const fieldMetadata = useMemo(
    () => normalizeImageFieldMetadata(rawFieldMetadata),
    [rawFieldMetadata],
  );

  const {
    assetReference,
    style = {},
    allowedImageTypes = ['icon'],
    versions = {},    // ✅ correct asset rendering
    dimensions = null // ✅ intrinsic width/height saved from upload
  } = fieldMetadata;

  const assetReferenceId = getAssetReferenceId(assetReference);

  const isPublicPreviewRuntime = useMemo(
    () => readIsPublicPreviewRuntime(),
    [],
  );

  /* ──────────────────────────────────────────────────────────────
     Enterprise preview resolver bridge
     - Authenticated builder/editor: resolve by assetId through backend
       getAssetById / useAssetPreview so private and base-plan assets
       render consistently.
     - Public preview/headless screenshot: skip backend auth calls and
       rely on durable public Storage paths below.
     - This does NOT change the persisted image metadata schema.
    ────────────────────────────────────────────────────────────── */
  const assetPreviewRef = useMemo(() => {
    if (isPublicPreviewRuntime) return {};
    if (!assetReferenceId) return {};
    return { assetId: assetReferenceId };
  }, [assetReferenceId, isPublicPreviewRuntime]);

  const {
    url: backendPreviewUrl,
    loading: backendPreviewLoading,
    error: backendPreviewError,
  } = useAssetPreview(assetPreviewRef);

  // ------------------------------------------------------------
  // Which image type are we showing?  (defaults to 'icon')
  
  const activeImageType = allowedImageTypes.includes('icon')
    ? 'icon'
    : allowedImageTypes.includes('banner')
    ? 'banner'
    : 'icon';

// ❶  Wrapper box size (icons only)
// - MIN width: 128px
// - MAX width: 250px
// - Height: fixed tile height (e.g., 128px or style.height override)
const MIN_ICON_WIDTH = 128;
const MAX_ICON_WIDTH = 250;

// Allow the theme / metadata to override the tile height if you want
const ICON_HEIGHT =
  Number.parseFloat(String(style.height || '128').replace('px', '')) || 128;

const iconBoxSize = dimensions
  ? {
      // clamp intrinsic width into [128, 250]
      width: Math.max(
        MIN_ICON_WIDTH,
        Math.min(dimensions.width || MIN_ICON_WIDTH, MAX_ICON_WIDTH),
      ),
      height: ICON_HEIGHT,
    }
  : {
      width : MIN_ICON_WIDTH,
      height: ICON_HEIGHT,
    };


  // 1×1 aspect-ratio banner – height is clamped in CSS
  const bannerClamp = { height: 'clamp(120px, 20vw, 240px)' };

  // ── Shared layout constants (must be declared before logMetrics)
  const paddingPx = parseInt(style.padding || '0', 10);

  const fixedBoxSize =
    activeImageType === 'banner'
      ? {}            // banner fills width; height via clamp
      : iconBoxSize;

  // NEW: inner radius so the inner clip matches the outer curve
  const outerRadiusPx =
    Number.parseFloat(String(style.borderRadius || '8').replace('px', '')) || 8;
  const innerRadiusPx = Math.max(0, outerRadiusPx - paddingPx);


  // Inner content box after padding
  const innerW = Math.max(1, (fixedBoxSize.width  || 0) - 2 * paddingPx);
  const innerH = Math.max(1, (fixedBoxSize.height || 0) - 2 * paddingPx);
  const innerAspect = innerW / innerH;

  // Icon intrinsic aspect (fallback to 1 if unknown)
  const iconW = Number(dimensions?.width  || 0);
  const iconH = Number(dimensions?.height || 0);
  const iconAspect = iconW > 0 && iconH > 0 ? iconW / iconH : 1;

  // Decide scaling mode for icons:
  // true → width-driven (icon wider than box), false → height-driven
  const useWidthDrivenIcon = iconAspect > innerAspect;

  /* ──────────────────────────────────────────────────────────────
     Enterprise URL resolution strategy (no eager prefetch):
     - We derive Storage object paths for all possible renditions.
     - We resolve ONLY ONE candidate path at a time via useStorageUrl().
     - On resolve failure or <img> error, we rotate to the next candidate.
     This prevents:
       • 403 spam from paths we never display
       • wasted work per render
       • racey behavior across public vs authed contexts
  ────────────────────────────────────────────────────────────── */
  const pathOriginal = versions?.original?.path || null;
  const pathThumbnail =
    fieldMetadata?.thumbnail && !isBrowserUrl(fieldMetadata.thumbnail)
      ? fieldMetadata.thumbnail
      : null;
  const pathIcon     = versions?.icon?.path     || versions?.thumbnail?.path || null;
  const pathIcon2x   = versions?.icon2x?.path   || null;
  const pathBanner   = versions?.banner?.path   || null;
  const pathBanner2x = versions?.banner2x?.path || null;

// fallbacks for newer schema / fast-first-paint
const thumbUrlCandidates = [
  fieldMetadata?.thumbUrl,
  fieldMetadata?.assetReference?.thumbUrl,
  fieldMetadata?.thumb?.urls?.['400'],
  fieldMetadata?.assetReference?.thumb?.urls?.['400'],
].filter(Boolean);

const directThumbUrl =
  thumbUrlCandidates.find(isBrowserUrl) || null;

const thumb400PathCandidate =
  fieldMetadata?.thumb?.urls?.['400'] ||
  fieldMetadata?.assetReference?.thumb?.urls?.['400'] ||
  null;

const thumb400Path =
  thumb400PathCandidate && !isBrowserUrl(thumb400PathCandidate)
    ? thumb400PathCandidate
    : null;

/* ──────────────────────────────────────────────────────────────
   ENTERPRISE (screenshot + runtime reliability)
   Candidate PATH rotation (resolve ONE at a time):
   - We do NOT pre-resolve every rendition via useStorageUrl()
     because that causes:
       • 403 spam for paths we never render
       • wasted work per render
       • racey behavior across public vs authed contexts
   - Instead, we pick ONE best candidate (path or direct URL),
     resolve it, and rotate on failure.
  ────────────────────────────────────────────────────────────── */
const candidatePaths = useMemo(() => {
  const dpr =
    (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1;

  // ICON candidates (fast-first)
  if (activeImageType === 'icon') {
    return [
      // Prefer new unified thumbs first
      thumb400Path || null,
      pathThumbnail || null,

      // Then fall back to originals / icon renditions
      pathOriginal || null,
      pathIcon2x || null,
      pathIcon || null,
    ].filter(Boolean);
  }

  // BANNER candidates:
  // - For screenshot/public preview: ALWAYS fast-first; avoid banner2x as primary
  // - For interactive browsing: DPR-aware (prefer sharper source on retina),
  //   but still keep thumb400 early for fast paint and fallback.
  if (isPublicPreviewRuntime) {
    return [
      thumb400Path || null,    // smallest and fastest
      pathBanner || null,      // 1x banner
      pathBanner2x || null,    // 2x banner (largest)
      pathOriginal || null,
    ].filter(Boolean);
  }

  // Interactive: DPR-aware preference
  if (dpr >= 1.5) {
    return [
      pathBanner2x || null,    // sharp first on retina (interactive only)
      pathBanner || null,
      thumb400Path || null,
      pathOriginal || null,
    ].filter(Boolean);
  }

  // Non-retina interactive
  return [
    pathBanner || null,
    thumb400Path || null,
    pathBanner2x || null,
    pathOriginal || null,
  ].filter(Boolean);
}, [
  activeImageType,
  isPublicPreviewRuntime,
  thumb400Path,
  pathThumbnail,
  pathOriginal,
  pathIcon2x,
  pathIcon,
  pathBanner,
  pathBanner2x,
]);

// NEW: candidate rotation index (enterprise reliability)
const [srcIndex, setSrcIndex] = useState(0);

// Candidate path for this render cycle (or null while none exist)
const selectedPath = candidatePaths?.[srcIndex] || null;

// ENTERPRISE: if a signed/backend URL expires or fails, burn it so we can fall back to paths
const [backendUrlFailed, setBackendUrlFailed] = useState(false);
const [directUrlFailed, setDirectUrlFailed] = useState(false);

const hasUsableBackendPreview =
  Boolean(backendPreviewUrl) &&
  !backendUrlFailed;

const hasUsableDirectThumb =
  Boolean(directThumbUrl) &&
  !directUrlFailed;

// ENTERPRISE:
// Derive public/private image policy from system-backed asset semantics.
// Image fields currently use icon/banner form-image assets, both of which are
// public-form-image types on the backend. If future image fields support
// private DAM types, this falls back safely because useStorageUrl only treats
// explicit public form-image types as public.
const selectedStorageAssetType =
  fieldMetadata?.assetReference?.systemAssetType ||
  fieldMetadata?.assetReference?.assetType ||
  fieldMetadata?.systemAssetType ||
  fieldMetadata?.assetType ||
  activeImageType;

// Do not resolve a Storage path when a higher-priority browser URL is already
// available. This prevents unnecessary Firebase Storage SDK calls and avoids
// storage/unauthorized console noise for public form-image assets.
const selectedPathForResolution =
  hasUsableBackendPreview || hasUsableDirectThumb
    ? null
    : selectedPath;

// Resolve the selected Storage path → URL (ONE call only)
const {
  url: resolvedUrl,
  error: resolveErr,
  source: resolvedUrlSource,
} = useStorageUrl(selectedPathForResolution, {
  assetType: selectedStorageAssetType,
  preferPublicUrl: true,
  allowPublicFallback: true,
  debugLabel: 'ImageFieldComponent.selectedPath',
});

// NOTE:
// - backendPreviewUrl is preferred for authenticated builder/editor paths.
// - If backendPreviewUrl fails in <img>, burn it and fall back to direct/path candidates.
// - directThumbUrl may be stale copied metadata, so it is only a fallback.
// - resolvedUrl comes from durable Storage path candidates.
// - Public-preview skips backendPreviewUrl and uses durable/public paths.
//
// ENTERPRISE: signed URLs can expire; if either backendPreviewUrl or directThumbUrl
// fails once, burn that source so we can fall back to resolved durable paths.
const assetUrl =
  (hasUsableBackendPreview ? backendPreviewUrl : null) ||
  (hasUsableDirectThumb ? directThumbUrl : null) ||
  resolvedUrl ||
  null;

/*  ↓↓↓  OPTIONAL: dev-log, comment out in production  ↓↓↓           */
//console.log('[ImageFieldComponent] path→url', {
//  selectedPath,
//  selectedPathForResolution,
//  selectedStorageAssetType,
//  resolvedUrl,
//  resolvedUrlSource,
//  resolveErr,
//  directThumbUrl,
//  backendPreviewUrl,
//});

/* local loading / error state so we can hide the <img> on 404 */
const [imgLoaded, setImgLoaded] = useState(false);
const [hadError , setHadError ] = useState(false);


// DEBUG REFS: measure actual layout boxes
const borderRef = useRef(null); // outer border box (has border + padding)
const innerRef  = useRef(null); // inner image wrapper (clipping box)
const imgRef    = useRef(null); // <img> element


const candidatePathsSignature = useMemo(
  () => (candidatePaths || []).join('|'),
  [candidatePaths],
);

/* reset when the actual image source set changes */
useEffect(() => {
  setImgLoaded(false);
  setHadError(false);
  setSrcIndex(0);
  setBackendUrlFailed(false);
  setDirectUrlFailed(false);
}, [
  fieldId,
  assetReferenceId,
  activeImageType,
  backendPreviewUrl,
  directThumbUrl,
  candidatePathsSignature,
]);

// ENTERPRISE: when the chosen URL changes, it must load again
useEffect(() => {
  setImgLoaded(false);
  setHadError(false);
}, [assetUrl]);

// ENTERPRISE: if resolving the selected path fails, rotate to next candidate path
useEffect(() => {
  if (!selectedPathForResolution) return;

  // Resolve error means we could not obtain a usable URL for this path
  // (storage/unauthorized, object-not-found, etc.)
  if (resolveErr) {
    // rotate to next candidate if available
    if (srcIndex < (candidatePaths?.length || 0) - 1) {
      setImgLoaded(false);
      setHadError(false);
      setSrcIndex((i) => i + 1);
      return;
    }

    // Exhausted candidates → mark error
    setHadError(true);
  }
}, [resolveErr, selectedPathForResolution, srcIndex, candidatePaths]);

  // DEBUG: measure layout & log numbers to find right-edge crop
  const logMetrics = React.useCallback((reason) => {
    try {
      const borderEl = borderRef.current;
      const innerEl  = innerRef.current;
      const imgEl    = imgRef.current;

      const borderRect = borderEl?.getBoundingClientRect?.() || {};
      const innerRect  = innerEl?.getBoundingClientRect?.() || {};
      const imgRect    = imgEl?.getBoundingClientRect?.() || {};

      const cs = borderEl ? getComputedStyle(borderEl) : null;

/*
      console.log('[ImageFieldComponent][metrics]', {
        reason,
        fieldId,
        activeImageType,
        // declared styles/inputs
        paddingPx, borderRadius: style.borderRadius || '8px',
        borderWidth: style.borderWidth || '1px',
        fixedBoxSize,
        // border box
        borderRect: {
          x: borderRect.x, y: borderRect.y,
          w: borderRect.width, h: borderRect.height,
        },
        // inner clipping box
        innerRect: {
          x: innerRect.x, y: innerRect.y,
          w: innerRect.width, h: innerRect.height,
        },
        // image
        imgRect: {
          x: imgRect.x, y: imgRect.y,
          w: imgRect.width, h: imgRect.height,
          natural: imgEl ? { w: imgEl.naturalWidth, h: imgEl.naturalHeight } : null,
        },
        // computed padding/border on border box (just to confirm at runtime)
        computed: cs ? {
          paddingTop   : cs.paddingTop,
          paddingRight : cs.paddingRight,
          paddingBottom: cs.paddingBottom,
          paddingLeft  : cs.paddingLeft,
          borderTopWidth   : cs.borderTopWidth,
          borderRightWidth : cs.borderRightWidth,
          borderBottomWidth: cs.borderBottomWidth,
          borderLeftWidth  : cs.borderLeftWidth,
          borderRadius     : cs.borderRadius,
        } : null,
      });
*/

    } catch (e) {
      console.warn('[ImageFieldComponent] logMetrics failed:', e);
    }
  }, [fieldId, activeImageType, paddingPx, style.borderRadius, style.borderWidth, fixedBoxSize]);

  // Log once after layout paints when an image has loaded (or reloaded)
  useLayoutEffect(() => {
    if (imgLoaded && !hadError) {
      // wait a tick for layout to settle
      const id = setTimeout(() => logMetrics('layoutEffect'), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [imgLoaded, hadError, logMetrics]);

/* ------------------------------------------------------------
   ❷  Render the selected asset (or zero-state / skeleton)
   ------------------------------------------------------------ */
  const renderSelectedAsset = () => {
    const hasRenderableAsset =
      Boolean(assetReferenceId) ||
      Boolean(directThumbUrl) ||
      Boolean(selectedPath);

    if (!hasRenderableAsset) {
      /* Zero-state box (same dimensions so layout doesn’t jump) */
      const zeroDims =
        activeImageType === 'banner'
          ? { width: '100%' }
          : iconBoxSize;

      return (
        <Box
          className="asset-card-container"
          sx={{
            width : '100%',
            height: '100%',
            border: '1px dashed #ccc',
            borderRadius : '8px',
            backgroundColor: '#ffffff',
            display      : 'flex',
            flexDirection: 'column',
            alignItems   : 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 1,
          }}
        >
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            No image selected
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Size:{' '}
            {activeImageType === 'banner'
              ? 'Full width × ~30% height (140–320 px)'
              : `${zeroDims.width} × ${zeroDims.height} px`}
          </Typography>
        </Box>
      );
    }

    /* ----------------------------------------------------------
       Enterprise note:
       - srcSet is intentionally omitted in this resolver model.
       - We resolve ONE URL at a time; using srcSet would require
         resolving multiple URLs in parallel, which reintroduces
         the exact 403/noise/race issues we fixed.
       - Candidate rotation already provides high-DPI fallback.
    ---------------------------------------------------------- */
    let srcSet = undefined;

    /* ------------------------------------------------------------------
       While the signed URL is still resolving, show a neutral skeleton.
       Once we have a URL, mount <img>.  When it finishes loading the
       skeleton disappears – no broken image icon, no flicker.
    ------------------------------------------------------------------ */
const noSourceAfterBackend =
  Boolean(assetReferenceId) &&
  !backendPreviewLoading &&
  !hasUsableBackendPreview &&
  !hasUsableDirectThumb &&
  !selectedPath;

const shouldShowUnavailable =
  (hadError && !assetUrl) ||
  noSourceAfterBackend;

const showSkeleton =
  !shouldShowUnavailable &&
  (!assetUrl || !imgLoaded);

    /* --------------------------------------------------------
       BANNER vs ICON <img> rules
       - BANNER: cover (unchanged)
       - ICON  : height-driven (fill height, width auto) to avoid
                 right-edge clipping with rounded corners + padding
    ---------------------------------------------------------*/
    const imgStyles =
      activeImageType === 'banner'
        ? {
            width         : '100%',
            height        : '100%',
            objectFit     : 'cover',
            objectPosition: 'center',
            display       : imgLoaded && !hadError ? 'block' : 'none',
          }
        : (
            useWidthDrivenIcon
              ? {
                  // ICON: width-driven (wider than box)
                  display       : imgLoaded && !hadError ? 'block' : 'none',
                  width         : '100%',
                  height        : 'auto',
                  maxWidth      : '100%',
                  objectFit     : 'contain',
                  objectPosition: 'center',
                }
              : {
                  // ICON: height-driven (taller/narrower than box)
                  display       : imgLoaded && !hadError ? 'block' : 'none',
                  height        : '100%',
                  width         : 'auto',
                  maxHeight     : '100%',
                  objectFit     : 'contain',
                  objectPosition: 'center',
                }
          );


    return (
  <Box
    ref={innerRef}
    sx={{
      position     : 'relative',
      width        : '100%',
      height       : '100%',

      // Use inner radius so the inner curve matches the outer minus padding
      borderRadius : `${innerRadiusPx}px`,
      overflow     : 'hidden',

      // Center the image
      display      : 'flex',
      alignItems   : 'center',
      justifyContent: 'center',

      // AA nudge on some DPIs (harmless)
      outline      : '1px solid transparent',
    }}

  >


        {shouldShowUnavailable && (
          <Box
            data-asset-unavailable="true"
            sx={{
              position : 'absolute',
              inset    : 0,
              bgcolor  : '#f5f5f5',
              color    : 'text.secondary',
              display  : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 1,
            }}
          >
            <Typography variant="caption" color="textSecondary">
              Image unavailable
            </Typography>
          </Box>
        )}

        {/* lightweight grey skeleton */}
        {showSkeleton && (
          <Box
            data-asset-skeleton="true"
            sx={{
              position : 'absolute',
              inset    : 0,
              bgcolor  : '#e0e0e0',
            }}
          />
        )}


                {assetUrl && (
          <img
            ref={imgRef}
            src={assetUrl}
            {...(srcSet ? { srcSet } : {})}
            alt="Selected"
            style={imgStyles}
            onLoad={() => {
              setImgLoaded(true);
              // log after onLoad so we know sizes
              requestAnimationFrame(() => logMetrics('img onLoad'));
            }}
            onError={(e) => {

              console.warn('[ImageFieldComponent] <img> error:', assetUrl);

              // ENTERPRISE: if the backend signed preview URL failed
              // (expired/revoked/not yet readable), burn it so the next
              // render can fall back to direct/path candidates.
              if (!backendUrlFailed && backendPreviewUrl && assetUrl === backendPreviewUrl) {
                setBackendUrlFailed(true);
                setImgLoaded(false);
                setHadError(false);
                return;
              }

              // ENTERPRISE: if the direct signed URL failed (expired/revoked),
              // burn it so the next render can fall back to resolved paths.
              if (!directUrlFailed && directThumbUrl && assetUrl === directThumbUrl) {
                setDirectUrlFailed(true);
              }

              // ENTERPRISE: rotate to next candidate before giving up
              if (srcIndex < (candidatePaths?.length || 0) - 1) {
                setImgLoaded(false);
                setHadError(false);
                setSrcIndex((i) => i + 1);
                return;
              }

              // Exhausted candidates → mark error
              setHadError(true);
            }}

          />
        )}
      </Box>
    );
  };

  // ------------------------------------------------------------
  // ❸  Guard for missing metadata
  // ------------------------------------------------------------
  if (!fieldMetadata) {
    console.error(
      '[ImageFieldComponent] Missing field metadata for field ID:',
      fieldId,
    );
    return (
      <Typography variant="body2" color="error">
        Error: Missing field metadata.
      </Typography>
    );
  }

  // ------------------------------------------------------------
  // ❹  OUTER containers (margins → border box → inner wrapper/img)
  // ------------------------------------------------------------

  return (
    <Box
      sx={{
        /* NEW → let the flex item fill the row when this is a banner */
        width: activeImageType === 'banner' ? '100%' : 'fit-content',

        mt: style.marginTop    || 0,
        mb: style.marginBottom || 0,
        ml: style.marginLeft   || 0,
        mr: style.marginRight  || 0,
      }}
    >
      <Box
        ref={borderRef}
        sx={{
          /* ── BORDER BOX ───────────────────────────────────────── */
          width : activeImageType === 'banner'
            ? '100%'
            : `${fixedBoxSize.width}px`,
          height: activeImageType === 'banner'
            ? bannerClamp.height               // aspect-ratio + clamp
            : `${fixedBoxSize.height}px`,
          border: `${style.borderWidth || '1px'} solid ${
            style.borderColor || '#ccc'
          }`,
          borderRadius : style.borderRadius || '8px',
          boxSizing    : 'border-box',   // ← padding sits *inside* width/height
          padding      : `${paddingPx}px`,
          textAlign    : 'center',
          display      : 'flex',
          alignItems   : 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        }}
      >
        {renderSelectedAsset()}
      </Box>
    </Box>
  );
};

ImageFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
};

export default ImageFieldComponent;
